import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Button,
    FormLayout,
    Input,
    LoadingProgress,
    Modal,
    Select,
    Switch,
} from "@/ui";
import { omit } from "../../utils/utils";
import { useMutation, useQuery } from "@apollo/react-hooks";
import useMeta from "../../base/hooks/useMeta";
import useForm from "../../base/hooks/form/useForm";
import useValidator from "../../base/hooks/form/useValidator";
import useNotification from "../../base/hooks/components/useNotification";
import { roleOptions } from "../../config/constant";
import {
    QUERY_USER_LIST,
    MUTATION_CREATE_USER,
    MUTATION_UPDATE_USER,
    QUERY_USER_GET,
} from "../../apollo/module-operations/user";
import { QUERY_STORES_FROM_ACCOUNT_LIST } from "../../apollo/module-operations/store";
import _ from "lodash";
import styles from "../store/GeneralsForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

const toStoreIds = value => {
    if (value == null || value === "") return [];
    const list = Array.isArray(value) ? value : [value];
    return list.map(String).filter(Boolean);
};

const UserFormModal = ({ isOpen, toggle, userId, storeId }) => {
    const { user } = useMeta();
    const notify = useNotification();
    const [isFormReset, resetFormInputs] = useState(userId);
    const [storeOptions, setStoreOptions] = useState([]);

    const {
        inputs,
        setInputs,
        inputChange,
        selectValue,
        selectChange,
        genericChange,
    } = useForm();

    const isSuperAdmin = Boolean(user?.roles?.find(r => r === "SUPER_ADMIN"));
    const roles = useMemo(
        () =>
            isSuperAdmin
                ? roleOptions
                : roleOptions.filter(r => r.value !== "SUPER_ADMIN"),
        [isSuperAdmin],
    );

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "first_name",
                method: "isEmpty",
                validWhen: false,
                message: "This first name is required.",
            },
            {
                field: "last_name",
                method: "isEmpty",
                validWhen: false,
                message: "This last name is required.",
            },
            {
                field: "email",
                method: "isEmpty",
                validWhen: false,
                message: "This email is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    useEffect(() => {
        if (!userId && !isFormReset) {
            resetFormInputs(true);
            setInputs({ enabled: true });
        }
    }, [userId, isFormReset, setInputs]);

    let options = {
        variables: {
            input: {
                ...inputs,
                account_id: user.accountId,
                store_id: user.storeId,
            },
        },
    };

    const { loading: userLoading } = useQuery(QUERY_USER_GET, {
        variables: {
            user_id: userId,
        },
        skip: !userId,
        fetchPolicy: "network-only",
        onCompleted: resp => {
            setInputs(resp.getUser);
        },
    });

    useQuery(QUERY_STORES_FROM_ACCOUNT_LIST, {
        variables: {
            account_id: user.accountId,
        },
        skip: user.loading || !user.accountId || !user.storeId,
        onCompleted: resp => {
            let ops = _.map(resp.listStoresFromAccount, v => {
                return { value: String(v.id), label: v.name };
            });

            setStoreOptions(ops);
        },
    });

    const [mutate, { loading }] = useMutation(
        userId !== null ? MUTATION_UPDATE_USER : MUTATION_CREATE_USER,
        {
            refetchQueries: [
                {
                    query: QUERY_USER_LIST,
                    variables: {
                        account_id: user.accountId,
                        store_id: user.storeId,
                    },
                },
            ],
            onCompleted: () => {
                resetFormInputs(false);
                isSubmited(false);
                toggle(false);

                notify(
                    `User ${
                        userId !== null ? "Updated" : "Created"
                    } Successfully"`,
                    "success",
                );
            },
            onError: () => {
                notify(
                    `Error ${
                        userId !== null ? "Updating" : "Creating"
                    }  The User`,
                    "error",
                );
            },
        },
    );

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            options = omit(options);
            mutate(options);
        }
    };

    const cancel = () => {
        resetFormInputs(false);
        isSubmited(false);
        toggle(false);
    };

    const onChangeStores = ids => {
        genericChange(ids ?? [], "store_id");
    };

    const selectedStoreIds = useMemo(
        () => toStoreIds(inputs.store_id),
        [inputs.store_id],
    );

    const autocompleteOptions = useMemo(() => {
        const known = new Set(storeOptions.map(o => o.value));
        const extras = selectedStoreIds
            .filter(id => !known.has(id))
            .map(id => ({ value: id, label: id }));
        return [...storeOptions, ...extras];
    }, [storeOptions, selectedStoreIds]);

    const roleSelectOptions = toSelectOptions(roles);
    const selectedRole =
        selectValue(inputs.role, roles)?.value ??
        (inputs.role ? String(inputs.role) : undefined);

    return (
        <Modal
            footer={
                <>
                    <Button
                        disabled={loading}
                        onClick={cancel}
                        type="button"
                        variant="secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={loading}
                        loading={loading}
                        onClick={createOrUpdate}
                        type="button"
                        variant="primary"
                    >
                        {userId ? "Update" : "Create"}
                    </Button>
                </>
            }
            onOpenChange={open => {
                if (!open) cancel();
            }}
            open={Boolean(isOpen)}
            size="lg"
            title={userId ? "Update User" : "Create User"}
        >
            {userLoading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <Switch
                        checked={Boolean(inputs?.enabled)}
                        label="Active"
                        name="enabled"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={2}>
                        <Input
                            error={fieldError(validator, "first_name")}
                            fullWidth
                            label="First Name"
                            name="first_name"
                            onChange={inputChange}
                            value={inputs.first_name ?? ""}
                        />
                        <Input
                            error={fieldError(validator, "last_name")}
                            fullWidth
                            label="Last Name"
                            name="last_name"
                            onChange={inputChange}
                            value={inputs.last_name ?? ""}
                        />
                        <Input
                            error={fieldError(validator, "email")}
                            fullWidth
                            label="Email"
                            name="email"
                            onChange={inputChange}
                            value={inputs.email ?? ""}
                        />
                        <Select
                            fullWidth
                            label="Role"
                            onValueChange={value =>
                                selectChange({ value }, "role")
                            }
                            options={roleSelectOptions}
                            value={
                                selectedRole != null
                                    ? String(selectedRole)
                                    : undefined
                            }
                        />
                    </FormLayout.Grid>
                    {!storeId ? (
                        <Autocomplete
                            allowCreate
                            fullWidth
                            label="Stores"
                            maxVisibleChips={2}
                            multiple
                            name="store_id"
                            onChange={onChangeStores}
                            options={autocompleteOptions}
                            placeholder="Store"
                            value={selectedStoreIds}
                        />
                    ) : null}
                </FormLayout>
            )}
        </Modal>
    );
};

export default UserFormModal;
